import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, Star, Users, TrendingUp, Trophy, QrCode, Copy, Check, Loader2, X, Upload, BarChart3, Link2, MessageSquare, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import FileDropzone, { IMAGE_UPLOAD } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const ReviewTracker = ({ profile, setProfile }) => {
  const [tab, setTab] = useState('staff');
  const [staff, setStaff] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [feedback, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qrStaff, setQrStaff] = useState(null);
  const [copied, setCopied] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [googleLink, setGoogleLink] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({ name: '', photo_url: '', role: '' });

  useEffect(() => { fetchStaff(); fetchAnalytics(); fetchFeedback(); }, []);
  useEffect(() => { if (profile) setGoogleLink(profile.google_review_link || ''); }, [profile]);

  const fetchStaff = async () => {
    try { const res = await axios.get(`${API}/staff`); setStaff(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAnalytics = async () => {
    try { const res = await axios.get(`${API}/staff/analytics`); setAnalytics(res.data); }
    catch (e) { console.error(e); }
  };

  const fetchFeedback = async () => {
    try { const res = await axios.get(`${API}/staff/feedback`); setFeedbackList(res.data); }
    catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.put(`${API}/staff/${editingId}`, form);
        setStaff(staff.map(s => s.id === editingId ? res.data : s));
        toast.success('Staff updated');
      } else {
        const res = await axios.post(`${API}/staff`, form);
        setStaff([...staff, res.data]);
        toast.success('Staff added!');
      }
      resetForm();
      fetchAnalytics();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/staff/${id}`);
      setStaff(staff.filter(s => s.id !== id));
      toast.success('Staff removed');
      fetchAnalytics();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleUploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, photo_url: res.data.url }));
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSaveGoogleLink = async () => {
    setSavingLink(true);
    try {
      const res = await axios.put(`${API}/profile`, { google_review_link: googleLink });
      setProfile(res.data);
      toast.success('Google Review link saved!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSavingLink(false); }
  };

  const getReviewLink = (s) => `${BASE}/review/${profile?.username}/${s.id}`;

  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(getReviewLink(s));
      setCopied(s.id);
      toast.success('Link copied!');
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const resetForm = () => { setForm({ name: '', photo_url: '', role: '' }); setEditingId(null); setShowForm(false); };

  const topPerformer = analytics.length > 0 ? analytics[0] : null;
  const totalReviews = analytics.reduce((sum, a) => sum + a.total_reviews, 0);

  return (
    <div className="max-w-4xl" data-testid="review-tracker-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Review Tracker</h1>
          <p className="text-[#A1A1AA] mt-1 text-sm">Track reviews &amp; capture private feedback</p>
        </div>
        <Button data-testid="add-staff-btn" onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-violet-600 hover:bg-violet-700 rounded-xl">
          <Plus className="w-4 h-4 mr-1" /> Add Staff
        </Button>
      </div>

      {/* Google Review Link Setup */}
      <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <Star className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Google Business Review Link</p>
            <p className="text-[#71717A] text-xs">4-5 star ratings redirect to Google. 1-3 stars capture private feedback.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input data-testid="google-review-link" value={googleLink} onChange={e => setGoogleLink(e.target.value)}
            className="bg-[#1E1E1E] border-[#27272A] text-white text-sm"
            placeholder="https://g.page/r/YOUR_BUSINESS/review" />
          <Button data-testid="save-google-link" onClick={handleSaveGoogleLink} disabled={savingLink}
            className="bg-violet-600 hover:bg-violet-700 rounded-xl px-6 shrink-0">
            {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[{ key: 'staff', label: 'Staff Members' }, { key: 'analytics', label: 'Analytics' }, { key: 'feedback', label: `Feedback${feedback.length > 0 ? ` (${feedback.length})` : ''}` }].map(t => (
          <button key={t.key} data-testid={`tracker-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              tab === t.key ? 'bg-violet-600 text-white' : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white border border-[#27272A]'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* QR Modal */}
      {qrStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setQrStaff(null)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#121212] border border-[#27272A] rounded-2xl w-full max-w-sm p-6 text-center">
            <h3 className="text-white font-semibold mb-1">{qrStaff.name}</h3>
            <p className="text-[#71717A] text-xs mb-4">Scan to leave a review</p>
            <div className="bg-white rounded-xl p-4 inline-block mb-4">
              <QRCodeSVG value={getReviewLink(qrStaff)} size={200} level="H"
                fgColor="#000000" bgColor="#ffffff" />
            </div>
            <p className="text-[#525252] text-[10px] break-all mb-4">{getReviewLink(qrStaff)}</p>
            <Button onClick={() => { copyLink(qrStaff); setQrStaff(null); }}
              className="w-full bg-violet-600 hover:bg-violet-700 rounded-xl">
              <Copy className="w-4 h-4 mr-2" /> Copy Link
            </Button>
          </motion.div>
        </div>
      )}

      {/* Staff Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] p-5 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-semibold">{editingId ? 'Edit Staff' : 'Add Staff Member'}</h2>
            <button onClick={resetForm}><X className="w-5 h-5 text-[#71717A]" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[#A1A1AA] text-xs">Name *</Label>
              <Input data-testid="staff-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="Staff name" />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-xs">Role</Label>
              <Input data-testid="staff-role" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                className="mt-1 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="e.g. Server, Bartender" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#A1A1AA] text-xs">Photo</Label>
              <FileDropzone
                {...IMAGE_UPLOAD}
                compact
                testId="staff-photo-dropzone"
                label={form.photo_url ? 'Replace photo or drag a new image' : 'Choose photo or drag and drop'}
                disabled={uploading}
                onUploaded={(result) => setForm(prev => ({ ...prev, photo_url: result.url }))}
                successMessage="Photo uploaded"
                className="mt-1"
              />
              <div className="mt-1 flex items-center gap-3">
                {form.photo_url && <img src={resolveMediaUrl(form.photo_url)} alt="" className="w-12 h-12 rounded-full object-cover border border-[#27272A]" />}
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="border-[#27272A] text-[#A1A1AA] text-xs rounded-lg">
                  {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  {form.photo_url ? 'Change' : 'Upload'}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => handleUploadPhoto(e.target.files?.[0])} />
              </div>
            </div>
          </div>
          <Button data-testid="save-staff-btn" onClick={handleSave} disabled={saving}
            className="w-full rounded-xl bg-violet-600 hover:bg-violet-700 h-10">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editingId ? 'Update Staff' : 'Add Staff'}
          </Button>
        </motion.div>
      )}

      {/* ══════ STAFF TAB ══════ */}
      {tab === 'staff' && (
        loading ? (
          <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : staff.length === 0 && !showForm ? (
          <div className="text-center py-20 bg-[#121212] rounded-2xl border border-[#27272A]">
            <Users className="w-12 h-12 text-[#525252] mx-auto mb-4" />
            <h3 className="text-white font-semibold text-lg mb-2">No staff members yet</h3>
            <p className="text-[#71717A] text-sm mb-4">Add your team to start tracking review performance</p>
            <Button onClick={() => setShowForm(true)} className="bg-violet-600 hover:bg-violet-700 rounded-xl">
              <Plus className="w-4 h-4 mr-1" /> Add Staff
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {staff.map((s, i) => {
              const a = analytics.find(x => x.staff_id === s.id);
              return (
                <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="rounded-xl bg-[#121212] border border-[#27272A] p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#1E1E1E] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {s.photo_url ? (
                        <img src={resolveMediaUrl(s.photo_url)} alt={s.name} className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-5 h-5 text-[#525252]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold text-sm">{s.name}</h3>
                      {s.role && <p className="text-[#71717A] text-xs">{s.role}</p>}
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        {a && a.total_reviews > 0 ? (
                          <>
                            <span className="flex items-center gap-1 text-amber-400">
                              <Star className="w-3 h-3 fill-current" /> {a.avg_rating}
                            </span>
                            <span className="text-[#71717A]">{a.total_reviews} reviews</span>
                          </>
                        ) : (
                          <span className="text-[#525252]">No reviews yet</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => copyLink(s)} className="p-2 rounded-lg hover:bg-white/5" title="Copy review link">
                        {copied === s.id ? <Check className="w-4 h-4 text-green-400" /> : <Link2 className="w-4 h-4 text-[#A1A1AA]" />}
                      </button>
                      <button data-testid={`qr-${s.id}`} onClick={() => setQrStaff(s)} className="p-2 rounded-lg hover:bg-white/5" title="QR Code">
                        <QrCode className="w-4 h-4 text-[#A1A1AA]" />
                      </button>
                      <button onClick={() => { setForm({ name: s.name, photo_url: s.photo_url || '', role: s.role || '' }); setEditingId(s.id); setShowForm(true); }}
                        className="p-2 rounded-lg hover:bg-white/5" title="Edit">
                        <Edit2 className="w-4 h-4 text-[#A1A1AA]" />
                      </button>
                      <button onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-red-500/10" title="Delete">
                        <Trash2 className="w-4 h-4 text-red-400/60" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )
      )}

      {/* ══════ ANALYTICS TAB ══════ */}
      {tab === 'analytics' && (
        <div className="space-y-5">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 text-center">
              <p className="text-2xl font-bold text-white">{totalReviews}</p>
              <p className="text-[#71717A] text-xs mt-1">Total Reviews</p>
            </div>
            <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 text-center">
              <p className="text-2xl font-bold text-white">{staff.length}</p>
              <p className="text-[#71717A] text-xs mt-1">Team Members</p>
            </div>
            <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 text-center">
              {topPerformer ? (
                <>
                  <div className="flex items-center justify-center gap-1">
                    <Trophy className="w-4 h-4 text-amber-400" />
                    <p className="text-lg font-bold text-white truncate">{topPerformer.name}</p>
                  </div>
                  <p className="text-[#71717A] text-xs mt-1">Top Performer</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-[#525252]">--</p>
                  <p className="text-[#71717A] text-xs mt-1">Top Performer</p>
                </>
              )}
            </div>
          </div>

          {/* Leaderboard */}
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5">
            <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-violet-400" /> Performance Leaderboard
            </h3>
            {analytics.length > 0 ? (
              <div className="space-y-3">
                {analytics.map((a, i) => (
                  <div key={a.staff_id} className="flex items-center gap-4 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.05] transition-colors">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-gray-400/20 text-gray-300' : i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-[#27272A] text-[#71717A]'
                    }`}>
                      #{i + 1}
                    </div>
                    <div className="w-9 h-9 rounded-full bg-[#1E1E1E] flex-shrink-0 overflow-hidden">
                      {a.photo_url ? <img src={resolveMediaUrl(a.photo_url)} alt="" className="w-full h-full object-cover" /> : <Users className="w-4 h-4 text-[#525252] m-auto mt-2.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{a.name}</p>
                      <p className="text-[#71717A] text-xs">{a.role}</p>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-current" />
                          <span className="text-white font-semibold text-sm">{a.avg_rating}</span>
                        </div>
                        <p className="text-[#525252] text-[10px]">avg</p>
                      </div>
                      <div className="text-center">
                        <span className="text-white font-semibold text-sm">{a.total_reviews}</span>
                        <p className="text-[#525252] text-[10px]">reviews</p>
                      </div>
                      {/* Rating bar */}
                      <div className="w-24 hidden sm:block">
                        <div className="flex gap-0.5">
                          {[5, 4, 3, 2, 1].map(star => {
                            const pct = a.total_reviews > 0 ? (a.rating_distribution[star] / a.total_reviews) * 100 : 0;
                            return (
                              <div key={star} className="flex-1 h-6 rounded-sm overflow-hidden bg-[#1E1E1E]">
                                <div className="w-full transition-all"
                                  style={{ height: `${pct}%`, backgroundColor: star >= 4 ? '#22C55E' : star === 3 ? '#F59E0B' : '#EF4444', marginTop: `${100 - pct}%` }} />
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[#525252] text-[8px] text-center mt-0.5">5 4 3 2 1</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#71717A] text-sm text-center py-8">No review data yet. Add staff and share their review links!</p>
            )}
          </div>
        </div>
      )}
      {/* ══════ FEEDBACK TAB ══════ */}
      {tab === 'feedback' && (
        <div className="space-y-4" data-testid="feedback-tab">
          <div className="bg-[#121212] rounded-2xl border border-[#27272A] p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-orange-500/15 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Private Feedback</p>
                <p className="text-[#71717A] text-xs">Reviews with 1-3 stars are kept private with written feedback</p>
              </div>
            </div>
            {feedback.length > 0 ? (
              <div className="space-y-3">
                {feedback.map((f, i) => (
                  <motion.div key={f.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="rounded-xl bg-white/[0.03] border border-[#27272A] p-4" data-testid={`feedback-item-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1E1E1E] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {f.staff_photo ? (
                          <img src={resolveMediaUrl(f.staff_photo)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Users className="w-4 h-4 text-[#525252]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-white text-sm font-medium">{f.staff_name}</p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`w-3 h-3 ${s <= f.rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />
                            ))}
                          </div>
                        </div>
                        {f.reviewer_name && (
                          <p className="text-[#71717A] text-xs flex items-center gap-1 mt-0.5">
                            <User className="w-3 h-3" /> {f.reviewer_name}
                          </p>
                        )}
                        {f.feedback && (
                          <p className="text-[#A1A1AA] text-sm mt-2 leading-relaxed">"{f.feedback}"</p>
                        )}
                        <p className="text-[#525252] text-[10px] mt-2">
                          {new Date(f.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="w-10 h-10 text-[#525252] mx-auto mb-3" />
                <p className="text-[#71717A] text-sm">No private feedback yet</p>
                <p className="text-[#525252] text-xs mt-1">When customers rate 1-3 stars, their feedback appears here</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewTracker;
